/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";

import { VehicleDetailsDto } from "@/components/DetailsSection/types";
import { useEmailTransferStore } from "@/stores/useEmailTransferStore";

import { ROUTES } from "../../../../../shared";
import BannerMessage, {
  BANNER_MESSAGE_STATUSES
} from "../../../../BannerMessage/BannerMessage";
import ActivateVehicleBanner from "../components/ActivateVehicleBanner/ActivateVehicleBanner";
import CompanyCardsDetails from "../components/CompanyCardsDetails/CompanyCardsDetails";
import DetailsCard from "../components/DetailsCard";
import EmailTransferDetails from "../components/EmailTransferDetails/EmailTransferDetails";
import AnalysisProvidersGeneralDetails from "../components/GeneralDetails/AnalysisProvidersGeneralDetails";
import CompaniesGeneralDetails from "../components/GeneralDetails/CompaniesGeneralDetails";
import DriverGeneralDetails from "../components/GeneralDetails/DriverGeneralDetails";
import IntegrationsGeneralDetails from "../components/GeneralDetails/IntegrationsGeneralDetails";
import VehicleGeneralDetails from "../components/GeneralDetails/VehicleGeneralDetails";
import ResourceScheduleCards from "../components/ResourceScheduleCards/ResourceScheduleCards";
import CompanySettings from "../components/Settings/CompanySettings/CompanySettings";
import ConnectionSettings from "../components/Settings/IntegrationSettings/ConnectionSettings";

const useDetailsContent = (route: ROUTES) => {
  const { t } = useTranslation();

  const emailTransferDetails = useEmailTransferStore(state => state.details);

  const renderGeneralDetails = (detailsData: any) => {
    switch (route) {
      case ROUTES.Vehicles:
        return <VehicleGeneralDetails detailsData={detailsData} />;
      case ROUTES.Drivers:
        return <DriverGeneralDetails detailsData={detailsData} />;
      case ROUTES.Companies:
        return <CompaniesGeneralDetails detailsData={detailsData} />;
      case ROUTES.Integrations:
        return <IntegrationsGeneralDetails detailsData={detailsData} />;
      case ROUTES.AnalysisProviders:
        return <AnalysisProvidersGeneralDetails detailsData={detailsData} />;

      default:
        return <></>;
    }
  };

  const renderSchedules = (detailsData: any, displayLoader: boolean) => {
    const validRoutes = [ROUTES.Vehicles, ROUTES.Drivers];

    if (!validRoutes.includes(route)) {
      return <></>;
    }

    return (
      <ResourceScheduleCards
        detailsData={detailsData}
        isLoading={displayLoader}
        pageLocation={route}
      />
    );
  };

  const renderCompanyCards = (detailsData: any, displayLoader: boolean) =>
    route === ROUTES.Companies ? (
      <CompanyCardsDetails
        detailsData={detailsData}
        isLoading={displayLoader}
      />
    ) : (
      <></>
    );

  const renderFileSyncContent = () => {
    return (
      <BannerMessage
        data-testid="quick-schedule-message"
        status={BANNER_MESSAGE_STATUSES.Info}
        title={t("Details##Feature not available in BETA version")}
        css={css({ margin: "24px 16px 24px 24px" })}
      />
    );
  };

  const renderEmailTransferContent = (companyId: string) => {
    return (
      emailTransferDetails && (
        <EmailTransferDetails
          emailTransferDetails={emailTransferDetails}
          companyId={companyId}
        />
      )
    );
  };

  const renderCompanySettings = (detailsData: any) => {
    return (
      <DetailsCard
        cardTitle={t("Details##company settings")}
        collapsable
        showExpandTooltip
        expandByDefault
      >
        <CompanySettings detailsData={detailsData} />
      </DetailsCard>
    );
  };

  const renderConnectionSettings = (detailsData: any) => {
    return route === ROUTES.Integrations ||
      route === ROUTES.AnalysisProviders ? (
      <ConnectionSettings detailsData={detailsData} route={route} />
    ) : (
      <></>
    );
  };

  const renderActivateVehicleBanner = (detailsData: VehicleDetailsDto) => {
    return <ActivateVehicleBanner detailsData={detailsData} />;
  };

  return {
    renderFileSyncContent,
    renderConnectionSettings,
    renderGeneralDetails,
    renderSchedules,
    renderCompanyCards,
    renderEmailTransferContent,
    renderCompanySettings,
    renderActivateVehicleBanner
  };
};

export default useDetailsContent;
